import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Bell,
  BellOff,
  BellRing,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Mail,
  MapPin,
  Monitor,
  Phone,
  Plus,
  Users,
} from 'lucide-react';
import { Job } from '../types';
import {
  AppointmentCategory,
  CATEGORY_STYLE,
  OperationsAlerts,
  PlannerAppointment,
  appointmentsOnDay,
  dayKey,
  formatClock,
  minutesFromMidnight,
} from '../lib/operationsAlerts';

const HOUR_HEIGHT = 64;
const GUTTER = 56;

interface Props {
  jobs: Job[];
  alerts: OperationsAlerts;
  onNavigate?: (tab: string) => void;
}

interface PositionedAppointment extends PlannerAppointment {
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

const startOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfDay(next);
};

function relativeUntil(date: Date): string {
  const ms = date.getTime() - Date.now();
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.floor(mins / 60);
  const rest = mins % 60;
  return `in ${hours}h${rest ? ` ${rest}m` : ''}`;
}

function layoutDay(appointments: PlannerAppointment[], startHour: number): PositionedAppointment[] {
  const sorted = [...appointments].sort((a, b) => a.start.getTime() - b.start.getTime());
  const groups: PlannerAppointment[][] = [];
  let current: PlannerAppointment[] = [];
  let currentEnd = -1;

  sorted.forEach(appointment => {
    const start = minutesFromMidnight(appointment.start);
    if (current.length && start >= currentEnd) {
      groups.push(current);
      current = [];
      currentEnd = -1;
    }
    current.push(appointment);
    currentEnd = Math.max(currentEnd, start + appointment.durationMins);
  });
  if (current.length) groups.push(current);

  const positioned: PositionedAppointment[] = [];
  groups.forEach(group => {
    const laneEnds: number[] = [];
    const assigned = group.map(appointment => {
      const start = minutesFromMidnight(appointment.start);
      let lane = laneEnds.findIndex(end => end <= start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = start + appointment.durationMins;
      return { appointment, lane };
    });
    const lanes = Math.max(1, laneEnds.length);
    assigned.forEach(({ appointment, lane }) => {
      positioned.push({
        ...appointment,
        lane,
        lanes,
        top: ((minutesFromMidnight(appointment.start) - startHour * 60) / 60) * HOUR_HEIGHT + 4,
        height: Math.max(40, (appointment.durationMins / 60) * HOUR_HEIGHT - 8),
      });
    });
  });
  return positioned;
}

const DayPlanner: React.FC<Props> = ({ jobs, alerts, onNavigate }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const appointments = useMemo(() => appointmentsOnDay(jobs, selectedDate), [jobs, selectedDate]);

  const { startHour, endHour } = useMemo(() => {
    const starts = appointments.map(a => minutesFromMidnight(a.start));
    const ends = appointments.map(a => minutesFromMidnight(a.end));
    // Keep a useful workday baseline, then expand for anything outside it.
    return {
      startHour: Math.max(0, Math.min(6, ...starts.map(m => Math.floor(m / 60)))),
      endHour: Math.min(24, Math.max(20, ...ends.map(m => Math.ceil(m / 60)))),
    };
  }, [appointments]);

  const positioned = useMemo(() => layoutDay(appointments, startHour), [appointments, startHour]);
  const selected = positioned.find(a => a.jobId === selectedJobId) || null;
  const isToday = dayKey(selectedDate) === dayKey(new Date());

  const now = new Date(nowTick);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const showNowLine = isToday && nowMinutes >= startHour * 60 && nowMinutes <= endHour * 60;
  const nowTop = ((nowMinutes - startHour * 60) / 60) * HOUR_HEIGHT;

  const alertArmed = appointments.filter(a => a.alertable).length;

  useEffect(() => {
    if (!scrollRef.current) return;
    if (appointments.length === 0) {
      scrollRef.current.scrollTop = 0;
      return;
    }
    const targetMinutes = isToday ? nowMinutes : 8 * 60;
    const targetTop = ((targetMinutes - startHour * 60) / 60) * HOUR_HEIGHT;
    scrollRef.current.scrollTop = Math.max(0, targetTop - 96);
    // Only re-centre when the day changes or the visible range shifts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, startHour, isToday, appointments.length]);

  const hours = Array.from({ length: Math.max(1, endHour - startHour) }, (_, index) => startHour + index);

  const dateHeading = selectedDate.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const toggleAlerts = () => alerts.setAlertsEnabled(!alerts.alertsEnabled);

  return (
    <section className="bg-white rounded-[32px] border border-stone-200 shadow-sm overflow-hidden">
      <div className="p-6 pb-5 border-b border-stone-100 bg-gradient-to-br from-white via-white to-stone-50">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-gecko" />
              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">Day Planner</p>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-bark mt-1">{dateHeading}</h3>
            <p className="text-xs font-medium text-stone-500 mt-1">
              {appointments.length === 0
                ? 'No appointments booked for this day.'
                : `${appointments.length} appointment${appointments.length === 1 ? '' : 's'} · ${alertArmed} armed for a 1-hour alert`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center rounded-xl border border-stone-200 bg-white p-1">
              <button
                onClick={() => setSelectedDate(day => addDays(day, -1))}
                className="p-2 rounded-lg text-stone-500 hover:bg-stone-100 transition"
                aria-label="Previous day"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setSelectedDate(startOfDay(new Date()))}
                className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-bark hover:bg-stone-100 rounded-lg transition"
              >
                Today
              </button>
              <button
                onClick={() => setSelectedDate(day => addDays(day, 1))}
                className="p-2 rounded-lg text-stone-500 hover:bg-stone-100 transition"
                aria-label="Next day"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => onNavigate?.('schedule')}
              className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-[10px] font-black uppercase tracking-widest transition"
            >
              <Plus className="w-3.5 h-3.5" /> Schedule
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          {(Object.keys(CATEGORY_STYLE) as AppointmentCategory[]).map(category => (
            <span key={category} className="inline-flex items-center gap-1.5 text-[10px] font-bold text-stone-500">
              <span className={`w-2 h-2 rounded-full ${CATEGORY_STYLE[category].dot}`} />
              {CATEGORY_STYLE[category].label}
            </span>
          ))}
        </div>
      </div>

      <div ref={scrollRef} className="overflow-y-auto overflow-x-auto custom-scrollbar max-h-[400px] sm:max-h-[520px]">
        <div
          className="relative w-full px-4 pb-6"
          style={{ height: (endHour - startHour) * HOUR_HEIGHT + 28 }}
        >
          {hours.map((hour, index) => {
            const label = new Date(2020, 0, 1, hour, 0, 0).toLocaleTimeString([], { hour: 'numeric' });
            return (
              <div key={hour} className="absolute left-0 right-0 flex items-start" style={{ top: index * HOUR_HEIGHT }}>
                <div className="w-14 pr-2 text-right text-[10px] font-black text-stone-400 -mt-1.5">{label}</div>
                <div className="flex-1 border-t border-stone-100" />
              </div>
            );
          })}

          {hours.map((hour, index) => (
            <div
              key={`half-${hour}`}
              className="absolute left-14 right-0 border-t border-dashed border-stone-100/80"
              style={{ top: index * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
            />
          ))}

          {appointments.length === 0 && (
            <div className="absolute inset-x-14 top-24 flex flex-col items-center justify-center rounded-3xl border border-dashed border-stone-200 bg-stone-50/70 py-10 text-center">
              <CalendarDays className="w-8 h-8 text-stone-300" />
              <p className="mt-3 text-sm font-black text-stone-500">Nothing booked for this day</p>
              <p className="text-[11px] text-stone-400 mt-1">Estimate visits and customer jobs appear here automatically.</p>
              <button
                onClick={() => onNavigate?.('schedule')}
                className="mt-4 px-4 py-2 bg-gecko text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-palm transition"
              >
                Open schedule
              </button>
            </div>
          )}

          {positioned.map(appointment => {
            const style = CATEGORY_STYLE[appointment.category];
            const isSelected = appointment.jobId === selectedJobId;
            return (
              <button
                key={`${appointment.jobId}-${appointment.start.toISOString()}`}
                onClick={() => setSelectedJobId(isSelected ? null : appointment.jobId)}
                className={`absolute rounded-xl border border-l-4 ${style.card} ${style.border} p-2 text-left shadow-sm hover:shadow-md transition overflow-hidden focus:outline-none focus:ring-2 focus:ring-gecko/40 ${
                  isSelected ? 'ring-2 ring-gecko/50 z-20' : 'z-10'
                }`}
                style={{
                  top: appointment.top,
                  height: appointment.height,
                  left: `calc(${GUTTER}px + (100% - ${GUTTER}px) * ${appointment.lane / appointment.lanes} + 4px)`,
                  width: `calc((100% - ${GUTTER}px) / ${appointment.lanes} - 9px)`,
                }}
                aria-label={`${formatClock(appointment.start)} ${appointment.title} ${style.label}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot} flex-shrink-0`} />
                  <span className="text-[10px] font-black text-stone-600 truncate">
                    {formatClock(appointment.start)} – {formatClock(appointment.end)}
                  </span>
                </div>
                <p className="text-xs font-black text-bark truncate mt-0.5">{appointment.title}</p>
                {appointment.height > 56 && (
                  <p className="text-[10px] font-semibold text-stone-500 truncate">
                    {style.label} · {appointment.durationMins}m
                  </p>
                )}
                {appointment.height > 82 && appointment.address && (
                  <p className="text-[10px] text-stone-400 truncate mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" /> {appointment.address}
                  </p>
                )}
              </button>
            );
          })}

          {showNowLine && (
            <div className="absolute left-10 right-2 z-30 pointer-events-none" style={{ top: nowTop }}>
              <div className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.15)]" />
                <div className="flex-1 border-t border-red-500" />
                <span className="ml-2 text-[9px] font-black text-red-500 uppercase tracking-wider">
                  {formatClock(now)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {selected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-stone-100"
          >
            <div className="p-5 bg-stone-50/80">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="min-w-0">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${CATEGORY_STYLE[selected.category].chip}`}>
                    {CATEGORY_STYLE[selected.category].label}
                  </span>
                  <h4 className="text-lg font-black text-bark mt-2 truncate">{selected.title}</h4>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] font-semibold text-stone-500">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {formatClock(selected.start)} – {formatClock(selected.end)} ({selected.durationMins}m)
                    </span>
                    {selected.address && (
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{selected.address}</span>
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {selected.others.join(', ')}
                    </span>
                  </div>
                  {selected.notes && (
                    <p className="text-xs text-stone-500 mt-2 line-clamp-2">{selected.notes}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone.replace(/[^\d+]/g, '')}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-stone-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-stone-700 hover:bg-stone-100 transition"
                    >
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                  )}
                  {selected.email && (
                    <a
                      href={`mailto:${selected.email}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-stone-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-stone-700 hover:bg-stone-100 transition"
                    >
                      <Mail className="w-3.5 h-3.5" /> Email
                    </a>
                  )}
                  <button
                    onClick={() => onNavigate?.('schedule')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-bark text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-stone-800 transition"
                  >
                    Open schedule
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="px-5 py-4 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <button
            role="switch"
            aria-checked={alerts.alertsEnabled}
            aria-label="One hour appointment alerts"
            onClick={toggleAlerts}
            className={`mt-0.5 w-10 h-5 rounded-full p-0.5 transition flex-shrink-0 ${
              alerts.alertsEnabled ? 'bg-gecko' : 'bg-stone-300'
            }`}
          >
            <span
              className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${
                alerts.alertsEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <div>
            <p className="text-[11px] font-black text-bark flex items-center gap-1.5">
              {alerts.alertsEnabled ? <BellRing className="w-3.5 h-3.5 text-gecko" /> : <BellOff className="w-3.5 h-3.5 text-stone-400" />}
              1-hour appointment alerts {alerts.alertsEnabled ? 'armed' : 'paused'}
            </p>
            <p className="text-[10px] text-stone-400">
              Only you are notified. Keep GreenTree open in a tab for the 1-hour chime.
            </p>
          </div>
        </div>

        {alerts.desktopPermission === 'granted' ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gecko/10 text-[#376E20] text-[10px] font-black uppercase tracking-widest">
            <Monitor className="w-3.5 h-3.5" /> Desktop alerts on
          </span>
        ) : alerts.desktopPermission === 'default' ? (
          <button
            onClick={alerts.requestDesktopPermission}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-stone-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-stone-800 transition"
          >
            <Bell className="w-3.5 h-3.5" /> Enable desktop alerts
          </button>
        ) : alerts.desktopPermission === 'denied' ? (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-stone-100 text-stone-500 text-[10px] font-black uppercase tracking-widest"
            title="Allow notifications for this site in your browser settings to get desktop alerts."
          >
            <BellOff className="w-3.5 h-3.5" /> Desktop alerts blocked
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-stone-100 text-stone-500 text-[10px] font-black uppercase tracking-widest">
            In-app alerts only
          </span>
        )}
      </div>
    </section>
  );
};

export default DayPlanner;
